import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";

const MENU_ESTIMATED_HEIGHT = 200;

// The menu floats with position: fixed next to its trigger so the scrolling table card never clips it.
const placeMenu = (trigger) => {
  if (!trigger || typeof window === "undefined") return {};
  const rect = trigger.getBoundingClientRect();
  const right = Math.max(8, window.innerWidth - rect.right);
  const opensUp = rect.bottom + MENU_ESTIMATED_HEIGHT > window.innerHeight && rect.top > MENU_ESTIMATED_HEIGHT;
  return opensUp ? { right, bottom: window.innerHeight - rect.top + 4 } : { right, top: rect.bottom + 4 };
};

/**
 * RowMenu: "⋯" button plus a floating menu (design RowMenu). It closes with Escape, on an outside click, on
 * scroll or resize and after choosing an option. `items` is a list of { key, label, icon, onSelect, disabled,
 * title } or the string "separator".
 */
export default function RowMenu({ label, open, onToggle, onClose, items }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [position, setPosition] = useState({});

  useLayoutEffect(() => {
    if (open) setPosition(placeMenu(triggerRef.current));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const firstItem = menuRef.current?.querySelector('[role="menuitem"]:not([disabled])');
    firstItem?.focus();

    const handlePointer = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      onClose();
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        onClose();
        triggerRef.current?.focus();
      }
    };
    const handleViewportChange = () => onClose();

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, onClose]);

  const moveFocus = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const enabled = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);
    if (!enabled.length) return;
    const index = enabled.indexOf(document.activeElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    enabled[(index + step + enabled.length) % enabled.length].focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="crm-rollo__btn crm-rollo__btn--ghost crm-rollo__btn--sm crm-rollo__btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={onToggle}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} aria-hidden="true" />
      </button>
      {open ? (
        <div ref={menuRef} className="crm-rollo__menu" role="menu" aria-label={label} style={position} onKeyDown={moveFocus}>
          {items.map((item, index) =>
            item === "separator" ? (
              <hr key={`separator-${index}`} className="crm-rollo__menu-sep" />
            ) : (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className="crm-rollo__menu-item"
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  onClose();
                  item.onSelect?.();
                }}
              >
                {item.icon ? <FontAwesomeIcon icon={item.icon} fixedWidth aria-hidden="true" /> : null}
                {item.label}
              </button>
            )
          )}
        </div>
      ) : null}
    </>
  );
}
